import PropTypes from 'prop-types';
import { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/components/hooks/useApp';
import { useAriaInboxSuggestions } from '@/components/ai/v2/useCockpitData';
import { formatDateShort } from '@/lib/utils';
import { 
  Sheet, 
  SheetContent, 
  SheetHeader, 
  SheetTitle,
  SheetTrigger 
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Bell, 
  AlertTriangle, 
  AlertCircle, 
  Info, 
  CheckCircle,
  X,
  ExternalLink,
  RefreshCw,
  Wallet,
  FileText,
  Users,
  Receipt,
  Shield,
  Inbox,
  Sparkles
} from 'lucide-react';
import { apiFetch } from '@/api/apiFetch';
import { toast } from 'sonner';

// Map a unified Signal Center InboxItem → the legacy AlertCard shape so the bell
// UI is unchanged. Severity: critical→critical, warning→high, info→low. Domain →
// the bell's category tabs (unmapped domains fall back to "cobros" in AlertCard).
const INBOX_SEVERITY_TO_BELL = { critical: 'critical', warning: 'high', info: 'low' };
const INBOX_DOMAIN_TO_CATEGORY = {
  invoicing: 'cobros', banking: 'tesoreria', fiscal: 'fiscal', hr: 'rrhh',
  accounting: 'gastos', crm: 'cobros', projects: 'cobros', inventory: 'gastos', general: 'cobros',
  // N-FE3 — el parte diario y demás señales de ARIA (dominio 'aria') llegan a su
  // propia categoría en la campana (items agregados: 1 por parte, no por acción).
  aria: 'aria',
};
function inboxItemToAlert(s) {
  return {
    id: s.id,
    severity: INBOX_SEVERITY_TO_BELL[s.severity] ?? 'medium',
    category: INBOX_DOMAIN_TO_CATEGORY[s.domain] ?? 'cobros',
    title: s.title,
    message: s.description,
    created_at: null,
    status: 'unread',
    // entity_id drives the "Ver detalle" button; reuse it as the nav flag and
    // carry the deep-link in action_url (handled in handleAction below).
    entity_id: s.action_url ? s.id : null,
    action_url: s.action_url ?? null,
  };
}

const SEVERITY_CONFIG = {
  critical: { 
    icon: AlertTriangle, 
    color: 'text-red-600', 
    bg: 'bg-red-50 border-red-200',
    badge: 'bg-red-100 text-red-700'
  },
  high: { 
    icon: AlertCircle, 
    color: 'text-orange-600', 
    bg: 'bg-orange-50 border-orange-200',
    badge: 'bg-orange-100 text-orange-700'
  },
  medium: { 
    icon: Info, 
    color: 'text-amber-600', 
    bg: 'bg-amber-50 border-amber-200',
    badge: 'bg-amber-100 text-amber-700'
  },
  low: { 
    icon: Info, 
    color: 'text-blue-600', 
    bg: 'bg-blue-50 border-blue-200',
    badge: 'bg-blue-100 text-blue-700'
  }
};

const CATEGORY_CONFIG = {
  cobros: { icon: Wallet, label: 'Cobros' },
  tesoreria: { icon: Receipt, label: 'Tesorería' },
  fiscal: { icon: Shield, label: 'Fiscal' },
  rrhh: { icon: Users, label: 'RRHH' },
  gastos: { icon: FileText, label: 'Gastos' },
  aria: { icon: Sparkles, label: 'ARIA' }
};

function AlertCard({ alert, onDismiss, onAction }) {
  const severity = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.medium;
  const category = CATEGORY_CONFIG[alert.category] || CATEGORY_CONFIG.cobros;
  const SeverityIcon = severity.icon;
  const CategoryIcon = category.icon;
  
  // Custom simple timeAgo since importing it might be tricky if not exported
  const timeAgo = (dateStr) => {
    if (!dateStr) return 'Ahora';
    
    try {
      const date = new Date(dateStr);
      
      // Verificar si la fecha es válida
      if (isNaN(date.getTime())) return 'Reciente';
      
      const now = new Date();
      const diff = now - date;
      
      // Si la diferencia es negativa o muy pequeña
      if (diff < 0) return 'Ahora';
      
      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);
      
      if (minutes < 1) return 'Ahora';
      if (minutes < 60) return `Hace ${minutes}m`;
      if (hours < 24) return `Hace ${hours}h`;
      if (days < 7) return `Hace ${days}d`;
      
      return formatDateShort(dateStr);
    } catch (e) {
      return 'Reciente';
    }
  };
  
  return (
    <div className={`p-4 rounded-lg border ${severity.bg} ${alert.status === 'unread' ? 'ring-2 ring-offset-1 ring-blue-200' : ''}`}>
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-full ${severity.bg}`}>
          <SeverityIcon className={`w-4 h-4 ${severity.color}`} />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className="text-xs">
              <CategoryIcon className="w-3 h-3 mr-1" />
              {category.label}
            </Badge>
            <span className="text-xs text-muted-foreground">{timeAgo(alert.created_at)}</span>
          </div>
          
          <h4 className="font-medium text-foreground text-sm">{alert.title}</h4>
          <p className="text-sm text-muted-foreground mt-1">{alert.message}</p>
          
          <div className="flex items-center gap-2 mt-3">
            {alert.entity_id && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => onAction(alert)}
                className="text-xs"
              >
                <ExternalLink className="w-3 h-3 mr-1" />
                Ver detalle
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => onDismiss(alert.id)}
              className="text-xs text-muted-foreground"
            >
              <X className="w-3 h-3 mr-1" />
              Descartar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * `open` / `onOpenChange` son OPCIONALES: sin ellos la campana gobierna su propio
 * Sheet (uso normal desde el TopBar); con ellos, quien la monta puede abrirla
 * —p.ej. el aviso de bienvenida (useAriaNudge)—.
 */
export default function NotificationCenter({ children, open: openProp, onOpenChange }) {
  const { t } = useTranslation();
  const { company } = useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [openInternal, setOpenInternal] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : openInternal;
  const setOpen = isControlled ? (onOpenChange ?? (() => {})) : setOpenInternal;
  const [activeTab, setActiveTab] = useState('all');

  // Signal Center unificado: misma queryKey que el orbe y la Bandeja, así que al
  // montar el badge ya tiene el contador real (antes solo cargaba al abrir el
  // Sheet → marcaba 0) y sin pedir nada extra: los tres comparten la caché.
  const { data, isLoading, isFetching, refetch } = useAriaInboxSuggestions(company?.id);

  const alerts = useMemo(
    () => (data?.suggestions ?? []).map(inboxItemToAlert),
    [data],
  );

  const stats = useMemo(() => {
    if (!data) return null;
    return {
      total: alerts.length,
      unread: alerts.length,
      bySeverity: {
        critical: alerts.filter((a) => a.severity === 'critical').length,
        high: alerts.filter((a) => a.severity === 'high').length,
        medium: alerts.filter((a) => a.severity === 'medium').length,
      },
    };
  }, [data, alerts]);

  const handleRefresh = async () => {
    if (!company?.id) return;
    try {
      // Unified bell: there's no client-side "detection" — just refetch the aggregator.
      await refetch();
    } catch (error) {
      toast.error(t('notifications.error_detecting'));
    }
  };

  const handleDismiss = async (alertId) => {
    try {
      // Persist the dismiss in the Signal Center (same store as the Bandeja).
      await apiFetch('/api/aria/dismiss', {
        method: 'POST',
        body: JSON.stringify({ suggestion_id: alertId }),
      });
      // Invalida la key compartida → el descarte se refleja también en la Bandeja
      // y en los nodos del orbe, no solo aquí.
      // eslint-disable-next-line local/no-literal-querykey -- key compartida con AriaInbox y el orbe (aria/inbox global)
      queryClient.invalidateQueries({ queryKey: ['aria-inbox', company?.id, 'all'] });
      toast.success(t('notifications.alert_dismissed'));
    } catch (error) {
      toast.error(t('notifications.error_dismissing'));
    }
  };

  const handleAction = (alert) => {
    // Unified bell: every item carries a deep-link in action_url. The aggregator
    // only ever emits internal paths ("/facturas?...", "/impuestos/..."); restrict
    // to leading-slash routes so a value can never become an off-site redirect.
    //
    // `startsWith('/')` no bastaba para eso: `//evil.com` y `/\evil.com` empiezan
    // por barra y el navegador los resuelve como protocol-relative, o sea fuera
    // del origen. La barra inicial tiene que ir seguida de algo que no sea otra
    // barra ni una contrabarra.
    if (typeof alert.action_url === 'string' && /^\/(?![/\\])/.test(alert.action_url)) {
      // El backend emite paths de raíz ("/facturas?..."); la SPA vive bajo
      // import.meta.env.BASE_URL ("/app/"), así que hay que anteponerlo.
      window.location.href = `${import.meta.env.BASE_URL.replace(/\/$/, '')}${alert.action_url}`;
    }
    setOpen(false);
  };

  const filteredAlerts = activeTab === 'all' 
    ? alerts 
    : alerts.filter(a => a.category === activeTab);

  const unreadCount = stats?.unread || 0;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {children || (
          <Button variant="ghost" size="icon" className="relative" aria-label="Notificaciones">
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              // `bg-destructive` y no `bg-red-500`: el rojo crudo de Tailwind
              // (#ef4444) deja el contador en 3.76:1 contra el blanco, por debajo
              // del 4.5 de AA. El token del sistema es más oscuro y sube a 4.8
              // sin cambiar la lectura del badge. Ver docs/UI_GUIDELINES.md §1.
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-destructive-foreground text-xs rounded-full flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </Button>
        )}
      </SheetTrigger>
      
      <SheetContent className="flex w-full max-w-[400px] flex-col overflow-hidden bg-card sm:max-w-[540px]">
        <SheetHeader className="shrink-0 pb-4">
          <div className="flex flex-wrap items-center justify-between gap-2 pr-5 sm:pr-0">
            <SheetTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Notificaciones
            </SheetTitle>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleRefresh}
              disabled={isFetching}
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
              {isFetching ? 'Actualizando...' : 'Actualizar'}
            </Button>
          </div>
          
          {/* Stats */}
          {stats && stats.total > 0 && (
            <div className="flex gap-2 mt-2">
              {stats.bySeverity.critical > 0 && (
                <Badge className="bg-red-100 text-red-700">
                  {stats.bySeverity.critical} críticas
                </Badge>
              )}
              {stats.bySeverity.high > 0 && (
                <Badge className="bg-orange-100 text-orange-700">
                  {stats.bySeverity.high} altas
                </Badge>
              )}
              {stats.bySeverity.medium > 0 && (
                <Badge className="bg-amber-100 text-amber-700">
                  {stats.bySeverity.medium} medias
                </Badge>
              )}
            </div>
          )}
        </SheetHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex min-h-0 flex-1 flex-col">
          <TabsList className="w-full shrink-0">
            <TabsTrigger value="all" className="flex-1">
              Todas ({alerts.length})
            </TabsTrigger>
            <TabsTrigger value="cobros" className="flex-1">
              Cobros
            </TabsTrigger>
            <TabsTrigger value="fiscal" className="flex-1">
              Fiscal
            </TabsTrigger>
            <TabsTrigger value="rrhh" className="flex-1">
              RRHH
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value={activeTab} className="mt-4 min-h-0 flex-1">
            <ScrollArea className="h-full">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : filteredAlerts.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-2" />
                  <p className="text-muted-foreground">No hay alertas pendientes</p>
                  <p className="text-sm text-muted-foreground mt-1">Todo está bajo control</p>
                </div>
              ) : (
                <div className="space-y-3 pr-4">
                  {filteredAlerts.map(alert => (
                    <AlertCard 
                      key={alert.id}
                      alert={alert}
                      onDismiss={handleDismiss}
                      onAction={handleAction}
                    />
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>

        {/* Footer: jump to the full Signal Center (same aggregator as the bell). */}
        <div className="mt-2 shrink-0 border-t pt-3">
          <Button
            variant="ghost"
            className="w-full justify-center text-sm"
            onClick={() => { setOpen(false); navigate('/aria/bandeja'); }}
          >
            <Inbox className="w-4 h-4 mr-2" />
            {t('notifications.view_all')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

NotificationCenter.propTypes = {
  children: PropTypes.node,
  open: PropTypes.bool,
  onOpenChange: PropTypes.func,
};

export { NotificationCenter };
